/**
 * Frecency (frequency + recency) tracker backed by localStorage.
 *
 * Each visit to a page/action is recorded with a timestamp. The score
 * decays exponentially so that recently visited items rank higher, but
 * frequently visited items still accumulate weight.
 *
 * Storage key: `nutshell:command-palette-frecency`
 */

import {readLocalStorage, writeLocalStorage} from '../../../../../util/localStorage';

const STORAGE_KEY = 'nutshell:command-palette-frecency';
const MAX_ENTRIES = 200;
const MAX_VISITS_PER_ENTRY = 20;
const HALF_LIFE_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

type FrecencyEntry = {
    id: string;
    visits: number[];
};

type FrecencyStore = {
    entries: Record<string, FrecencyEntry>;
};

function loadStore(): FrecencyStore {
    try {
        const raw = readLocalStorage(STORAGE_KEY);
        if (raw) {
            return JSON.parse(raw) as FrecencyStore;
        }
    } catch {
        // Corrupt data – start fresh
    }

    return {entries: {}};
}

function computeScore(entry: FrecencyEntry): number {
    const now = Date.now();
    let score = 0;

    for (const visitTime of entry.visits) {
        const age = now - visitTime;
        score += Math.exp((-age * Math.LN2) / HALF_LIFE_MS);
    }

    return score;
}

function evictIfNeeded(store: FrecencyStore) {
    const ids = Object.keys(store.entries);
    if (ids.length <= MAX_ENTRIES) return;

    const scored = ids
        .map((entryId) => {
            const storeEntry = store.entries[entryId];
            if (!storeEntry) return null;

            return {id: entryId, score: computeScore(storeEntry)};
        })
        .filter((s): s is {id: string; score: number} => s !== null);

    scored.sort((a, b) => b.score - a.score);
    const keep = new Set(scored.slice(0, MAX_ENTRIES).map((s) => s.id));

    for (const entryId of ids) {
        if (!keep.has(entryId)) {
            delete store.entries[entryId];
        }
    }
}

export function recordVisit(id: string) {
    const store = loadStore();
    const entry = store.entries[id] || {id, visits: []};

    entry.visits.push(Date.now());
    if (entry.visits.length > MAX_VISITS_PER_ENTRY) {
        entry.visits = entry.visits.slice(-MAX_VISITS_PER_ENTRY);
    }

    store.entries[id] = entry;
    evictIfNeeded(store);
    writeLocalStorage(STORAGE_KEY, JSON.stringify(store));
}

export function getFrecencyScores(): Map<string, number> {
    const store = loadStore();
    const scores = new Map<string, number>();

    for (const [id, entry] of Object.entries(store.entries)) {
        scores.set(id, computeScore(entry));
    }

    return scores;
}

export function getTopFrecent(count: number): string[] {
    const scores = getFrecencyScores();
    const sorted = Array.from(scores.entries()).sort((a, b) => b[1] - a[1]);

    return sorted.slice(0, count).map(([id]) => id);
}
